import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../contexts/NotificationContext';
import { supabase } from '../../services/supabase';
import { Company } from '../../types/company.types';
import { useFeatureFlag, FeatureFlag } from '../../utils/featureFlags';
import CustobarIntegration from './CustobarIntegration';

interface IntegrationsTabProps {
  companyId: string;
}

const IntegrationsTab = ({ companyId }: IntegrationsTabProps) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const isCustobarEnabled = useFeatureFlag(FeatureFlag.CUSTOBAR_DATA_INGESTION);

  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch company data
  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const { data, error } = await supabase
          .from('companies')
          .select('*')
          .eq('id', companyId)
          .single();

        if (error) throw error;

        setCompany(data);
      } catch (error) {
        console.error('Error fetching company:', error);
        showNotification(t('company.error_loading_company'), 'error');
      } finally {
        setLoading(false);
      }
    };

    if (companyId) {
      fetchCompany();
    }
  }, [companyId, t, showNotification]);

  const handleCompanyUpdate = (updatedCompany: Company) => {
    setCompany(updatedCompany);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">{t('common.loading')}</div>
      </div>
    );
  }

  if (!company) {
    return <div className="text-red-500">{t('company.company_not_found')}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-sm shadow p-4">
        <h3 className="text-xl font-semibold mb-4">{t('integrations.title')}</h3>
        <p className="text-gray-600 mb-6">{t('integrations.description')}</p>

        {/* Available Integrations */}
        <div className="space-y-2">
          {/* Custobar Integration */}
          {isCustobarEnabled && (
            <CustobarIntegration
              companyId={companyId}
              company={company}
              onCompanyUpdate={handleCompanyUpdate}
            />
          )}

          {/* Future integrations can be added here as additional components */}
        </div>

        {/* No integrations available message */}
        {!isCustobarEnabled && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg mb-2">
              {t('integrations.no_integrations_available')}
            </div>
            <div className="text-gray-500 text-sm">
              {t('integrations.contact_admin_for_integrations')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IntegrationsTab;
